import React, { useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useCollect, useRequest } from 'hooks';

import Show from 'components/Show';
import Progress from 'components/Misc/Progress';
import Nothing from 'components/Misc/Nothing';
import { Container } from 'components/Misc/Sortable';
import { Button } from 'components/Misc/Form';

import Distribution from 'containers/Shared/Exams/Profile/Tasks/Distribution';
import Task from 'containers/Shared/Exams/Profile/Tasks/Task';

import { useExamProfileStore } from 'store/exam/useExamProfileStore';
import { useTaskOrderStore } from 'store/task/useTaskOrderStore';

const Tasks = ({ tab, setTab }) => {
	// Cache exam's id
	const { id } = useParams();

	// Use router's history
	const history = useHistory();

	// Set tab on load
	useEffect(() => setTab(tab), [tab, setTab]);

	// Store request parameters
	const parameters = useMemo(() => ({
		examId: id
	}), [id]);

	// Store requested tasks
	const [[result, progress], collect] = useCollect('tasks', parameters);

	// Store put request
	const [[updateProgress], update] = useRequest('put', `exams/${id}`);

	// Use exam's profile store
	const editable = useExamProfileStore(state => state.exam.editable);

	// Use task's order store
	const [tasks, edited, onSort, onInit, onReset] = useTaskOrderStore(state => [
		state.tasks,
		state.edited,
		state.onSort,
		state.onInit,
		state.onReset
	]);

	// Collect tasks on mount and on parameter change
	useEffect(() => {
		if (parameters.examId) (async () => await collect())();
	}, [parameters, collect]);

	// Initialize task's data after collection
	useEffect(() => {
		if (result) onInit(result);

		// Reset exam's data on unmount
		return () => onReset();
	}, [result]);

	// Handles form's onSubmit event
	// @event: object | onSubmit event
	const onSubmit = async (event) => {
		event.preventDefault();

		// Do not submit if request in progress
		if (progress) return;

		// Try to update task
		const [success, error] = await update(tasks, { related: 'tasks-order' });

		// TODO: Think of a better way of handling success
		if (success && success.id !== Number(id)) {
			return history.push(`/exams/${success.id}`);
		}

		// TODO: Thin of a better way of handling errors
		if (error) return console.log(error.message);
	};

	// TODO: Remove after testing
	useEffect(() => console.log('EXAM.TASKS:', tasks), [tasks]);

	return (
		<div className="mt-4 p-4 space-y-4 bg-white dark:bg-neutral-800 rounded-lg transition-colors">
			{/* Show loading progress */}
			<Progress progress={progress} />

			{/* Show nothing found */}
			<Nothing visible={!tasks?.length && !progress} />

			{/* Show collection if results collected */}
			<Show if={tasks?.length && !progress}>
				{/* Exam's point distribution */}
				<Distribution tasks={tasks} />

				<Container
					lockAxis="y"
					lockOffset="0%"
					lockToContainerEdges
					useDragHandle
					onSortEnd={onSort}
					className="flex flex-col gap-4"
				>
					{tasks?.map((task, idx) =>
						<Task key={task.id} idx={idx} task={task} />
					)}
				</Container>
			</Show>
			
			{/* Show controls if not progress */}
			<Show if={!progress && editable}>
				<div className="flex justify-end space-x-2">
					<Button
						icon="add"
						value="Jauns uzdevums"
						to={`/exams/tasks/create/${id}`}
					/>

					<Button
						icon="done"
						theme="success"
						value="Saglabāt"
						disabled={!edited}
						progress={updateProgress}
						onClick={onSubmit}
					/>
				</div>
			</Show>
		</div>	
	);
};

export default Tasks;
